/**
 * Normalize an Axios/API error into a readable string suitable for
 * rendering inside a toast or inline alert.
 *
 * FastAPI 422 responses ship `detail` as an ARRAY of Pydantic v2 error
 * entries (`{type, loc, msg, input, ctx, url}`). Passing that array
 * (or a single entry) directly into `toast.error` — or any place that
 * ultimately renders it as a React child — crashes with:
 *   "Objects are not valid as a React child (found: object with keys
 *    {type, loc, msg, input, ctx, url})"
 *
 * This helper accepts every plausible detail shape:
 *   • string  — returned as-is
 *   • array of entries — each entry flattened as "loc: msg"
 *   • single entry object — flattened as "loc: msg"
 *   • unknown / missing — falls back to the provided default
 *
 * It never rethrows and never returns an object.
 */
export function formatApiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  const msg = flatten(detail);
  if (msg) return msg;
  // Some endpoints return {message: "..."} instead of {detail: "..."}.
  const m2 = err?.response?.data?.message;
  if (typeof m2 === "string" && m2.trim()) return m2;
  // Fall back to the axios error string / status text.
  if (err?.message && typeof err.message === "string") return err.message;
  return fallback;
}

function flatten(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(entryToString).filter(Boolean).join("; ");
  }
  if (typeof detail === "object") return entryToString(detail);
  return String(detail);
}

function entryToString(entry) {
  if (!entry) return "";
  if (typeof entry === "string") return entry;
  if (typeof entry !== "object") return String(entry);
  // Pydantic v2 entry: {type, loc, msg, input, ctx, url}
  const msg = typeof entry.msg === "string" ? entry.msg : "";
  const loc = Array.isArray(entry.loc)
    ? entry.loc.filter((p) => p !== "body").join(".")
    : "";
  if (msg && loc) return `${loc}: ${msg}`;
  if (msg) return msg;
  // Last-resort readable rendering.
  try { return JSON.stringify(entry); } catch { return String(entry); }
}
